/**
 * Screen-space comment surfaces (F-50/F-51), rendered OUTSIDE the camera
 * transform in the `.board-canvas` container (its containing block — same
 * contract as the F-27 toolbar; never portal to body, coordinates are
 * container-relative):
 *
 *  - COMMENT MODE (F-50): `C` or the top-bar button toggles it; a capture
 *    layer turns the cursor into a pin and a click hit-tests TOP-DOWN per
 *    docs/04 §3 (floating element → grid cell → bare board) and opens the
 *    new-thread composer there. Esc exits.
 *  - The THREAD POPOVER: a bubble anchored to a board point through
 *    boardToScreen, so it tracks pan/zoom exactly — you opened it BY clicking
 *    its pin, so the pin is on screen and the bubble belongs next to it.
 *  - The NEW-THREAD COMPOSER: the SAME kind of bubble, anchored to the draft
 *    pin — it opens where the click landed (the owner's ask, 2026-09-03). It
 *    spent a while docked to the bottom-right corner instead, over two
 *    objections to the anchored version: it covered the cells being commented
 *    on, and a pan carried it off screen while the click-away below discarded
 *    the half-typed draft. The second objection is gone — click-away no longer
 *    touches the composer (Esc, the × and a submit are the only ways out), so
 *    panning away to re-read context and panning back finds the box still
 *    beside its pin. The first is the trade the owner chose: a box beside the
 *    hand beats one across the screen — and since 2026-10-15 the bubble
 *    TOUCHES the pin on the side that covers the least of the commented
 *    cell/element (`placeBubble`), so it sits next to the target rather than
 *    on it.
 *  - Esc handling mirrors the F-27 popover pattern (window CAPTURE keydown,
 *    active only while something is open, stopPropagation so the grid never
 *    sees that Esc): composer → popover → comment mode, one level per press.
 *
 * This module also activates the F-27 add-comment hook for the mounted board
 * view (side-effect import of ./add-comment.js binds it at module scope).
 */
import { boardToScreen, screenToBoard, type Anchor } from "@kola/shared";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { useStore } from "zustand";

import {
  safeSetPointerCapture,
  TOUCH_SLOP_SCREEN_PX,
  type CanvasStore,
} from "../../core/canvas.js";
import type { BoardSession } from "../../core/session.js";
import { CloseButton } from "../../ui/CloseButton.js";
import { showToast } from "../../ui/toast.js";
import { activateCommentsUi } from "./add-comment.js";
import type { BoardRect } from "../../registries/toolbar-actions/index.js";
import {
  anchorTargetGone,
  hitTestCommentTarget,
  pinAtScreenPoint,
  placeBubble,
  resolveAnchorPosition,
  resolveAnchorTargetRect,
  type BoardPoint,
  type PinScreenTip,
} from "./anchoring.js";
import { PIN_SIZE_PX } from "./CommentPinsLayer.js";
import { pinWidthPx, threadFacepile } from "./facepile.js";
import { elementRectLookup, useGrabRects } from "./hooks.js";
import { resolvedElementRects } from "../elements/contained.js";
import type { CommentsUiStore } from "./store.js";
import { CommentInput, ThreadView } from "./ThreadPopover.js";

/** Pin-to-bubble spacing: the open pin's 2px outline, so the bubble meets
 * the ring instead of hiding one side of it. */
const BUBBLE_GAP_PX = 2;
/** Preferred inset from the board edges when a bubble slides along its pin. */
const BUBBLE_MARGIN_PX = 8;
/** Pre-measurement guess (CSS width × a short thread) — replaced before paint. */
const BUBBLE_SIZE_ESTIMATE = { width: 300, height: 180 };

export interface CommentOverlaysProps {
  session: BoardSession;
  canvas: CanvasStore;
  ui: CommentsUiStore;
}

export function CommentOverlays({ session, canvas, ui }: CommentOverlaysProps) {
  const camera = useStore(canvas, (s) => s.camera);
  const viewport = useStore(canvas, (s) => s.viewport);
  const mode = useStore(ui, (s) => s.mode);
  const draft = useStore(ui, (s) => s.draft);
  const openThreadId = useStore(ui, (s) => s.openThreadId);
  const layout = useStore(ui, (s) => s.gridLayout);
  const frozen = useStore(session.store, (s) => s.frozen);
  const commentsVersion = useStore(session.store, (s) => s.commentsVersion);
  const elementsVersion = useStore(session.store, (s) => s.elementsVersion);
  const grabRects = useGrabRects(session);

  // Route the F-27 add-comment hook to THIS board view while it is mounted.
  useEffect(() => activateCommentsUi(session, ui), [session, ui]);

  // F-45: the freeze blocks all comment writes — exit mode and drop an open
  // composer rather than letting them submit into a silent no-op.
  useEffect(() => {
    if (!frozen) return;
    const state = ui.getState();
    if (state.mode) state.setMode(false);
    if (state.draft !== null) state.closeDraft();
  }, [frozen, ui]);

  // F-50's "C" toggle now lives in ToolRail.tsx, alongside "V"/"P" — one
  // listener coordinating all three tools, so switching TO comment mode always
  // also turns draw mode off (tool-mode.ts `setActiveTool`). This effect only
  // ever turns comment mode OFF (Esc below), which needs no such coordination.

  // Esc: one comment surface per press — composer, then popover, then mode.
  // Window CAPTURE + stopPropagation (the F-27 pattern) so the grid's own
  // Esc-clears-selection never races these.
  const escActive = mode || draft !== null || openThreadId !== null;
  useEffect(() => {
    if (!escActive) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      const state = ui.getState();
      if (state.draft !== null) state.closeDraft();
      else if (state.openThreadId !== null) state.closeThread();
      else state.setMode(false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [escActive, ui]);

  // Click-away closes an open THREAD POPOVER (Miro behavior). Interactions
  // with the bubble itself, a pin, or the sidebar panel don't count.
  //
  // The composer is deliberately NOT in this: it holds text the user typed,
  // and the click that "dismissed" it was usually a pan to go re-read the
  // line being commented on. Esc, the × button and a successful submit close
  // it — a click on the board never throws the draft away. (This is also what
  // makes anchoring the composer to its pin safe again — see the module note.)
  useEffect(() => {
    if (openThreadId === null) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(".comment-bubble, .comment-pin, .comments-panel") != null) return;
      ui.getState().closeThread();
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [openThreadId, ui]);

  // The open thread, re-read on any comment change; the popover closes when
  // the thread vanishes (deleted remotely / last message deleted / undo).
  const thread = useMemo(() => {
    void commentsVersion;
    return openThreadId !== null ? session.board.getThread(openThreadId) : undefined;
  }, [session, commentsVersion, openThreadId]);
  useEffect(() => {
    if (openThreadId !== null && thread === undefined) ui.getState().closeThread();
  }, [openThreadId, thread, ui]);

  // Element rects for the bubbles — SAME resolver and inputs as the pins
  // layer (layout included: an image inside a cell takes its rect from the
  // cell, not from its stale x/y cache, which once opened its bubble far
  // from the pin), so bubble and pin can never disagree within a render pass.
  const getElementRect = useMemo(
    () => elementRectLookup(session, grabRects, layout),
    [session, grabRects, layout],
  );

  // The open thread's pin position, and the cell/element it is about (the
  // bubble keeps clear of that).
  const threadPosition = useMemo<BoardPoint | null>(() => {
    if (thread === undefined) return null;
    void elementsVersion;
    return resolveAnchorPosition(thread.anchor, getElementRect, layout);
  }, [thread, getElementRect, layout, elementsVersion]);
  const threadTarget = useMemo<BoardRect | null>(() => {
    if (thread === undefined) return null;
    void elementsVersion;
    return resolveAnchorTargetRect(thread.anchor, getElementRect, layout);
  }, [thread, getElementRect, layout, elementsVersion]);
  const draftTarget = useMemo<BoardRect | null>(() => {
    if (draft === null) return null;
    void elementsVersion;
    return resolveAnchorTargetRect(draft.anchor, getElementRect, layout);
  }, [draft, getElementRect, layout, elementsVersion]);

  // Fallback bubble position for a thread whose anchor cannot be resolved on
  // ANY client (target died before any layout pass saw them coexist): the
  // popover must still open — resolve/reply/delete stay reachable and the
  // panel's jump does SOMETHING (F-52 accessibility, C4 "never silently eat
  // the input") — so it opens at a synthetic point near the viewport center.
  const bubblePosition = useMemo<BoardPoint | null>(() => {
    if (thread === undefined) return null;
    if (threadPosition !== null) return threadPosition;
    return screenToBoard(camera, { x: viewport.width / 2, y: viewport.height * 0.4 });
  }, [thread, threadPosition, camera, viewport]);

  // C4 (docs/07): the composer's target was deleted WHILE the composer is
  // open — surface a notice instead of letting the submit silently vanish;
  // the submit below falls back to a board anchor at the draft pin position.
  const draftTargetGone = useMemo(() => {
    if (draft === null || draft.anchor.kind === "board") return false;
    void elementsVersion; // element existence lives outside React state
    return anchorTargetGone(draft.anchor, (id) => session.board.hasElement(id), layout);
  }, [draft, session, layout, elementsVersion]);

  /* ---------------- comment-mode click capture (F-50) ---------------- */

  /** Touch gesture on the mode layer: a pending tap (drop/open a pin) that
   * becomes a board PAN once it travels — one finger moves around, a clean
   * tap comments (mobile controls; desktop drops on pointerdown as before). */
  const modeNavRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    lastClientX: number;
    lastClientY: number;
    moved: boolean;
  } | null>(null);

  const onModePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.pointerType === "touch") {
      modeNavRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        lastClientX: event.clientX,
        lastClientY: event.clientY,
        moved: false,
      };
      safeSetPointerCapture(event.currentTarget, event.pointerId);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    dropOrOpenAt({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  };

  const onModePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const nav = modeNavRef.current;
    if (nav === null || nav.pointerId !== event.pointerId) return;
    if (canvas.getState().pinching) {
      modeNavRef.current = null; // the two-finger camera gesture owns it now
      return;
    }
    const dx = event.clientX - nav.lastClientX;
    const dy = event.clientY - nav.lastClientY;
    if (!nav.moved) {
      if (
        Math.abs(event.clientX - nav.startClientX) < TOUCH_SLOP_SCREEN_PX &&
        Math.abs(event.clientY - nav.startClientY) < TOUCH_SLOP_SCREEN_PX
      ) {
        return; // still a tap
      }
      nav.moved = true;
    }
    nav.lastClientX = event.clientX;
    nav.lastClientY = event.clientY;
    canvas.getState().panBy(dx, dy);
  };

  const onModePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const nav = modeNavRef.current;
    if (nav === null || nav.pointerId !== event.pointerId) return;
    modeNavRef.current = null;
    if (nav.moved || event.type !== "pointerup") return;
    // The tap's DOWN point is where the pin lands (the finger may microshift).
    const rect = event.currentTarget.getBoundingClientRect();
    dropOrOpenAt({ x: nav.startClientX - rect.left, y: nav.startClientY - rect.top });
  };

  const onModeLostCapture = (event: React.PointerEvent<HTMLDivElement>) => {
    if (modeNavRef.current?.pointerId === event.pointerId) modeNavRef.current = null;
  };

  const dropOrOpenAt = (screenPoint: { x: number; y: number }) => {
    const cameraNow = canvas.getState().camera;
    const point = screenToBoard(cameraNow, screenPoint);
    const uiState = ui.getState();
    // Miro behavior: clicking an EXISTING pin while in comment mode opens
    // that thread instead of dropping a second draft on top of it. The mode
    // capture layer sits above the pins, so the pin click is re-derived here
    // from the same resolver the pins render from (visibility rules included).
    const lookup = elementRectLookup(session, grabRects, uiState.gridLayout);
    const pins: PinScreenTip[] = [];
    for (const t of session.board.listThreads()) {
      if (t.messages.length === 0) continue; // mid-sweep, never rendered
      if (t.resolved && !uiState.showResolved) continue; // hidden pin (F-53)
      const pos = resolveAnchorPosition(t.anchor, lookup, uiState.gridLayout);
      if (pos === null) continue;
      // Same width the pin paints at — a facepile pin is wider than the base
      // teardrop, and its tail must take the click too.
      pins.push({
        threadId: t.id,
        tip: boardToScreen(cameraNow, pos),
        width: pinWidthPx(threadFacepile(t.messages), PIN_SIZE_PX),
      });
    }
    const hitThreadId = pinAtScreenPoint(screenPoint, pins, PIN_SIZE_PX);
    if (hitThreadId !== null) {
      uiState.openThread(hitThreadId);
      return;
    }
    // docs/04 §3 top-down hit-test: topmost element → cell → board. Contained
    // elements are hit-tested at their RESOLVED rect (cell + dx/dy), so a click
    // over a contained image anchors to the element, not the cell beneath it.
    const anchor = hitTestCommentTarget(
      point,
      // Same grab-aware rects as `lookup` above: a comment dropped on an
      // element a peer is dragging must anchor to where it is DRAWN.
      resolvedElementRects(session.board.listElements(), uiState.gridLayout, grabRects),
      uiState.gridLayout,
    );
    uiState.openDraft({ anchor, position: point });
  };

  return (
    <>
      {mode && (
        <div
          className="comment-mode-layer"
          onPointerDown={onModePointerDown}
          onPointerMove={onModePointerMove}
          onPointerUp={onModePointerUp}
          onPointerCancel={onModePointerUp}
          onLostPointerCapture={onModeLostCapture}
        />
      )}
      {draft !== null && (
        // Anchored to the draft pin's board point, like the thread popover:
        // the box opens where the click landed and tracks pan/zoom with it.
        <Bubble
          camera={camera}
          viewport={viewport}
          position={draft.position}
          pinWidth={PIN_SIZE_PX}
          target={draftTarget}
        >
          <div className="comment-composer" role="dialog" aria-label="New comment">
            <div className="comment-composer-head">
              <span className="comment-composer-title">New comment</span>
              <span className="comment-composer-target">
                {draftTargetLabel(draft.anchor.kind)}
              </span>
              <CloseButton
                label="Discard this comment (Esc)"
                onClick={() => ui.getState().closeDraft()}
              />
            </div>
            <div className="thread-view">
              {draftTargetGone && (
                <span
                  className="thread-orphan-note"
                  title="The cell or element this comment was aimed at was deleted while you typed"
                >
                  Target was deleted — will pin to the board
                </span>
              )}
              <CommentInput
                placeholder="Add a comment…"
                submitLabel="Comment"
                autoFocus
                disabled={frozen}
                onSubmit={(body) => {
                  const state = ui.getState();
                  // C4 composer race (docs/07): the anchor target was deleted
                  // while the composer was open (or in the same sync batch that
                  // delivered it). Re-validate at SUBMIT time — a thread created
                  // with a dead target id would be unresolvable on every client
                  // forever. Fall back to a board anchor at the draft pin's own
                  // position, created already-orphaned for the panel (F-52).
                  const gone =
                    draft.anchor.kind !== "board" &&
                    anchorTargetGone(
                      draft.anchor,
                      (id) => session.board.hasElement(id),
                      state.gridLayout,
                    );
                  const threadId = gone
                    ? session.createCommentThread(
                        { kind: "board", x: draft.position.x, y: draft.position.y },
                        body,
                        { orphaned: true },
                      )
                    : session.createCommentThread(draft.anchor, body);
                  if (gone && threadId !== null) {
                    showToast(
                      "The cell or element was deleted — comment pinned to the board at its last position",
                    );
                  }
                  state.closeDraft();
                  if (threadId !== null) state.openThread(threadId);
                }}
                onCancel={() => ui.getState().closeDraft()}
              />
            </div>
          </div>
        </Bubble>
      )}
      {thread !== undefined && bubblePosition !== null && (
        <Bubble
          key={thread.id}
          camera={camera}
          viewport={viewport}
          position={bubblePosition}
          pinWidth={pinWidthPx(threadFacepile(thread.messages), PIN_SIZE_PX)}
          target={threadTarget}
        >
          <ThreadView session={session} thread={thread} onClose={() => ui.getState().closeThread()} />
        </Bubble>
      )}
    </>
  );
}

/**
 * What the composer will attach to. The bubble already says most of this by
 * sitting next to the pin; the words stay because the pin is small and a
 * cell-vs-element distinction is worth being explicit about before submit.
 */
function draftTargetLabel(kind: Anchor["kind"]): string {
  if (kind === "cell") return "on a cell";
  if (kind === "element") return "on an element";
  return "on the board";
}

/**
 * A bubble anchored to a board point: positioned in screen px from
 * boardToScreen (tracks pan/zoom), TOUCHING the pin's box on whichever side
 * covers the least of the commented cell/element while staying on screen
 * (`placeBubble` — anchoring.ts has the rules). The bubble's own size is
 * measured — before the first paint, then whenever its content resizes it —
 * because a thread's height is content-driven and the side choice needs it.
 *
 * Used for the THREAD POPOVER and for the NEW-THREAD COMPOSER (anchored to
 * the draft pin) — see the module note.
 */
function Bubble({
  camera,
  viewport,
  position,
  pinWidth,
  target,
  children,
}: PropsWithChildren<{
  camera: { x: number; y: number; zoom: number };
  viewport: { width: number; height: number };
  position: BoardPoint;
  /** Painted pin width (a facepile pin is wider than the base teardrop). */
  pinWidth: number;
  /** The commented cell/element (board units), or null for a board anchor. */
  target: BoardRect | null;
}>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState(BUBBLE_SIZE_ESTIMATE);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const measure = () => {
      const width = el.offsetWidth;
      const height = el.offsetHeight;
      setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const tip = boardToScreen(camera, position);
  const targetTopLeft = target === null ? null : boardToScreen(camera, target);
  const placement = placeBubble({
    pin: { left: tip.x, top: tip.y - PIN_SIZE_PX, right: tip.x + pinWidth, bottom: tip.y },
    target:
      target === null || targetTopLeft === null
        ? null
        : {
            left: targetTopLeft.x,
            top: targetTopLeft.y,
            right: targetTopLeft.x + target.w * camera.zoom,
            bottom: targetTopLeft.y + target.h * camera.zoom,
          },
    bubble: size,
    viewport,
    gap: BUBBLE_GAP_PX,
    margin: BUBBLE_MARGIN_PX,
  });
  // `above` is pinned by its BOTTOM edge (see BubblePlacement.y).
  const style =
    placement.side === "above"
      ? { left: placement.left, top: placement.y, transform: "translateY(-100%)" }
      : { left: placement.left, top: placement.y };
  return (
    <div ref={ref} className={`comment-bubble comment-bubble-${placement.side}`} style={style}>
      {children}
    </div>
  );
}
